import React, { useRef } from "react";

type Option<T extends string> = { value: T; title: string; subtitle?: string; badge?: string };
type Props<T extends string> = { label: string; value: T; onChange: (v: T) => void; options: Option<T>[] };

export function SegmentedControl<T extends string>({ label, value, onChange, options }: Props<T>) {
    const refs = useRef<(HTMLButtonElement | null)[]>([]);
    const move = (i: number, dir: 1 | -1) => {
        const next = (i + dir + options.length) % options.length;
        onChange(options[next].value);
        refs.current[next]?.focus();
    };
    return (
        <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
            {options.map((o, i) => {
                const on = o.value === value;
                return (
                    <button
                        key={o.value}
                        ref={(el) => {
                            refs.current[i] = el;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        tabIndex={on ? 0 : -1}
                        onClick={() => onChange(o.value)}
                        onKeyDown={(e) => {
                            if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                                e.preventDefault();
                                move(i, 1);
                            }
                            if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                                e.preventDefault();
                                move(i, -1);
                            }
                        }}
                        className={`flex min-h-[56px] flex-col gap-0.5 rounded-md px-3 py-2 text-left ${on ? "bg-white shadow-sm ring-2 ring-max-900" : "text-gray-600"}`}
                    >
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                            {o.title}
                            {o.badge && (
                                <span className="rounded-full bg-max-900 px-2 py-px text-[11px] font-semibold text-white">
                                    {o.badge}
                                </span>
                            )}
                        </span>
                        {o.subtitle && <span className="text-xs">{o.subtitle}</span>}
                    </button>
                );
            })}
        </div>
    );
}
